import React, { Component } from "react";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { withGoogleReCaptcha } from "react-google-recaptcha-v3";
import TextInput from "../inputs/TextInput";
import { Button } from "../util/Clickable";
import { IntlText } from "../util/Text";
import * as validate from "../util/Validator";

class RegisterForm extends Component {
  state = {
    loading: false,
    recaptchaReady: false,
  };

  checkRecaptcha = () => {
    if (this.props.googleReCaptchaProps?.executeRecaptcha) {
      if (!this.state.recaptchaReady) {
        console.log("[DEBUG] RegisterForm: reCAPTCHA became available");
        this.setState({ recaptchaReady: true });
      }
      if (this.checkRecaptchaInterval) {
        clearInterval(this.checkRecaptchaInterval);
        this.checkRecaptchaInterval = null;
      }
      return true;
    }
    return false;
  };

  componentDidMount() {
    // 既に利用可能な場合は即座に設定
    if (this.checkRecaptcha()) {
      return;
    }

    // 定期的にチェック
    this.checkRecaptchaInterval = setInterval(() => {
      this.checkRecaptcha();
    }, 100);

    // 10秒後にタイムアウト
    this.recaptchaTimeout = setTimeout(() => {
      if (this.checkRecaptchaInterval) {
        clearInterval(this.checkRecaptchaInterval);
        this.checkRecaptchaInterval = null;
      }
      if (!this.state.recaptchaReady) {
        console.error("[DEBUG] RegisterForm: reCAPTCHA loading timeout", {
          hasGoogleReCaptchaProps: !!this.props.googleReCaptchaProps,
          hasExecuteRecaptcha:
            !!this.props.googleReCaptchaProps?.executeRecaptcha,
        });
      }
    }, 10000);
  }

  componentDidUpdate(prevProps) {
    // googleReCaptchaPropsが変更された場合に再チェック
    if (prevProps.googleReCaptchaProps !== this.props.googleReCaptchaProps) {
      console.log("[DEBUG] RegisterForm: googleReCaptchaProps changed");
      if (this.checkRecaptcha() && this.checkRecaptchaInterval) {
        clearInterval(this.checkRecaptchaInterval);
        this.checkRecaptchaInterval = null;
      }
    }
  }

  componentWillUnmount() {
    if (this.checkRecaptchaInterval) {
      clearInterval(this.checkRecaptchaInterval);
      this.checkRecaptchaInterval = null;
    }
    if (this.recaptchaTimeout) {
      clearTimeout(this.recaptchaTimeout);
      this.recaptchaTimeout = null;
    }
  }

  onSubmit = async (values) => {
    this.setState({
      loading: true,
    });

    try {
      // reCAPTCHA トークンを取得
      if (!this.props.googleReCaptchaProps?.executeRecaptcha) {
        console.error(
          "[DEBUG] RegisterForm: executeRecaptcha is not available",
          {
            hasGoogleReCaptchaProps: !!this.props.googleReCaptchaProps,
            recaptchaReady: this.state.recaptchaReady,
          },
        );
        this.setState({
          loading: false,
        });
        throw new SubmissionError({
          _error:
            "セキュリティチェックが利用できません。ページをリロードしてください。",
        });
      }

      console.log("[DEBUG] RegisterForm: Executing reCAPTCHA...");
      const token = await this.props.googleReCaptchaProps.executeRecaptcha(
        "register_form",
      );
      console.log(
        "[DEBUG] RegisterForm: reCAPTCHA token received:",
        token ? "***" + token.slice(-10) : "null",
      );

      if (!token) {
        console.error("[DEBUG] RegisterForm: reCAPTCHA token is empty");
        this.setState({
          loading: false,
        });
        throw new SubmissionError({
          _error:
            "セキュリティチェックのトークンが取得できませんでした。ページをリロードしてください。",
        });
      }

      const valuesWithToken = { ...values, recaptcha_token: token };

      return this.props.onSubmit(valuesWithToken).then(({ value, action }) => {
        if (value.status < 300) {
          this.setState(
            {
              loading: false,
            },
            () => {
              this.props.onSuccess &&
                this.props.onSuccess(value.data, value.response);
            },
          );
        } else {
          this.setState({
            loading: false,
          });
          const errors = Object.keys(value.errors).reduce((dic, key) => {
            dic[key] = Array.isArray(value.errors[key])
              ? value.errors[key].join("\n")
              : null;
            return dic;
          }, {});
          throw new SubmissionError(errors);
        }
      });
    } catch (error) {
      this.setState({
        loading: false,
      });
      throw error;
    }
  };

  render = () => {
    return (
      <form
        onSubmit={this.props.handleSubmit(this.onSubmit)}
        action={this.props.action}
        method="post"
      >
        <div className="uk-margin-small">
          <Field
            name="email"
            type="email"
            component={TextInput}
            labelId="forms.attr.email"
            validate={[validate.required, validate.email]}
            required
          />
        </div>

        {(() => {
          if (this.props.error) {
            return (
              <div className="uk-margin-small uk-text-danger uk-text-center@s">
                <span>{this.props.error}</span>
              </div>
            );
          }
        })()}
        <div className="uk-margin-small uk-text-center">
          <Button
            className="uk-button uk-button-default"
            type="submit"
            disabled={this.props.invalid || !this.state.recaptchaReady}
            ga={{ category: this.props.form, action: "submit" }}
            loading={this.state.loading ? 1 : 0}
          >
            <IntlText id="forms.auth.signup" />
          </Button>
          {!this.state.recaptchaReady && (
            <div className="uk-margin-small uk-text-muted uk-text-small">
              <span>
                セキュリティチェックを読み込み中...
                <br />
                長時間表示される場合はページをリロードしてください。
              </span>
            </div>
          )}
        </div>
      </form>
    );
  };
}

export default reduxForm({
  form: "register-form",
  enableReinitialize: true,
  withRef: true,
})(withGoogleReCaptcha(RegisterForm));
